export enum CampaignStatus {
  Draft = 'draft',
  Active = 'active',
  Completed = 'completed'
}

/**
 * Instrument lifecycle status — aligned to campaign-service Mongo enum.
 * Display (Sensemaker): draft → Draft, active → Live, paused → Pause,
 * completed → Completed (target reached), expired → Expired (end date),
 * stopped → Stopped (permanent sensemaker stop), closed → Closed (legacy).
 */
export enum InstrumentStatus {
  Draft = 'draft',
  Active = 'active',
  Paused = 'paused',
  Stopped = 'stopped',
  Completed = 'completed',
  Expired = 'expired',
  /** Legacy manual close — prefer `stopped` for BFP-616. */
  Closed = 'closed',
}

/** BFP-616 — mandatory reason when a sensemaker permanently stops an instrument. */
export enum InstrumentStopReason {
  SufficientResponsesCollected = 'sufficient_responses_collected',
  ResearchObjectivesChanged = 'research_objectives_changed',
  BudgetConstraints = 'budget_constraints',
  InstrumentError = 'instrument_error',
  CampaignCancelled = 'campaign_cancelled',
  DuplicateInstrument = 'duplicate_instrument',
  LowResponseQuality = 'low_response_quality',
  ComplianceLegalHold = 'compliance_legal_hold',
  ClientStakeholderRequest = 'client_stakeholder_request',
  Other = 'other',
}

export interface IStopInstrumentBody {
  stopReason: InstrumentStopReason
  stopReasonOther?: string | null
}

/** Still accepting responses or paused mid-flight — not terminal. */
export function isCollectingInstrumentStatus(status: string): boolean {
  return status === InstrumentStatus.Active || status === InstrumentStatus.Paused
}

/** Target met, timeline expired, or manually closed — no new SP starts. */
export function isTerminalInstrumentStatus(status: string): boolean {
  return (
    status === InstrumentStatus.Completed ||
    status === InstrumentStatus.Expired ||
    status === InstrumentStatus.Stopped ||
    status === InstrumentStatus.Closed
  )
}

export enum QuestionType {
  SingleSelect = 'single_select',
  MultiSelect = 'multi_select',
  Likert = 'likert',
  LikertMatrix = 'likert_matrix',
  Slider = 'slider',
  NPS = 'nps',
  StarRating = 'star_rating',
  DragRank = 'drag_rank',
  ShortText = 'short_text',
  LongText = 'long_text',
  ImageComparison = 'image_comparison'
}

export enum InsightConfidence {
  High = 'high',
  Medium = 'medium',
  Low = 'low'
}

export enum VerificationStatus {
  Unverified = 'unverified',
  Pending = 'pending',
  Verified = 'verified'
}

/** @deprecated Use Platform MS `sp_redemptions.status` — not persisted on legacy `IReward`. */
export enum RewardStatus {
  Pending = 'pending',
  Fulfilled = 'fulfilled',
  Failed = 'failed',
  Deferred = 'deferred'
}

/**
 * Coarse platform-level role (platform DB `platform_roles` / `PlatformUser.globalRoleId`).
 * Distinct from tenant Sensemaker RBAC (`ADMIN` / `EDITOR` / `VIEWER`).
 */
export enum PlatformRole {
  PlatformAdmin = 'PLATFORM_ADMIN',
  Sensemaker = 'SENSEMAKER',
  SignalProvider = 'SIGNAL_PROVIDER',
}

/** Canonical role strings stored on users / JWT `role` (uppercase). */
export enum UserRole {
  PlatformAdmin = 'PLATFORM_ADMIN',
  SensemakerAdmin = 'ADMIN',
  SensemakerEditor = 'EDITOR',
  SensemakerViewer = 'VIEWER',
  SignalProvider = 'SIGNAL_PROVIDER',
}

/** BFP-1006 — persona chosen during Sensemaker self-serve signup. */
export enum SensemakerSignupPersona {
  Individual = 'individual',
  Team = 'team',
  Company = 'company',
}

export interface ICampaignMember {
  userId: string
  role: UserRole
}

export interface ICampaign {
  _id: string
  name: string
  goal: string
  researchQuestions: string[]
  assumptions: string[]
  targetAudience: string
  budget: number
  budgetCommitted: number
  budgetSpent: number
  status: CampaignStatus
  owner: string
  members: ICampaignMember[]
  launchedAt?: Date
  createdAt: Date
  updatedAt: Date
}

export interface ILineOfInquiry {
  _id: string
  campaignId: string
  title: string
  description: string
  methodology: string
  status: 'active' | 'complete'
  budgetAllocation?: number
  createdAt: Date
  updatedAt: Date
}

export interface IInstrument {
  _id: string
  loiId: string
  campaignId: string
  name: string
  type: string
  status: InstrumentStatus
  targeting: {
    audienceCriteria: Record<string, unknown>
    sampleSize: number
  }
  timing: {
    startDate?: Date
    endDate?: Date
  }
  incentive: {
    amount: number
    type: string
  }
  estimatedCost: number
  actualCost: number
  surveyToken?: string
  publishedAt?: Date
  createdAt: Date
  updatedAt: Date
}

export interface IQuestionOption {
  label: string
  value: string
}

export interface IQuestionMetadata {
  semanticDomain?: string
  constructMapping?: string
  dataType?: string
}

export interface IQuestion {
  _id: string
  instrumentId: string
  text: string
  type: QuestionType
  options: IQuestionOption[]
  order: number
  required: boolean
  logic: Record<string, unknown>
  metadata: IQuestionMetadata
}

/** Response lifecycle (BFP-257 — supports in-progress drafts + resume). */
export enum ResponseStatus {
  InProgress = 'in_progress',
  Submitted = 'submitted'
}

export interface IAnswer {
  questionId: string
  /** Structured selection value(s). */
  value?: string | string[] | number
  /** Question type (BFP-257 — drives PII fan-out vs aggregation). */
  type?: 'open_text' | 'structured'
  /** Open-text body before PII sanitation. */
  rawText?: string
  /** Open-text body after PII sanitation. */
  sanitisedText?: string
}

export interface IResponse {
  _id: string
  instrumentId: string
  campaignId: string
  loiId: string
  profileId: string | null
  anonymousToken: string | null
  answers: IAnswer[]
  qualityScore: number
  /** BFP-257 — draft vs submitted. */
  status?: ResponseStatus
  /** BFP-257 — last/next question for resume. */
  currentQuestionId?: string | null
  /** BFP-257 — when the SP started the response. */
  startedAt?: Date
  /** Null while in-progress; set on submit. */
  submittedAt: Date | null
}

export interface IProfile {
  _id: string
  userId: string
  demographics: Record<string, unknown>
  preferences: string[]
  verificationStatus: VerificationStatus
  rewardBalance: number
  lifetimeEarnings: number
  surveysCompleted: number
  fcmToken?: string
  platform: 'web' | 'ios' | 'android'
  createdAt: Date
  updatedAt: Date
}

export interface IEvidence {
  responseId: string
  relevanceScore: number
}

export interface IQuantitativeSupport {
  frequency?: string
  pValue?: number
  testUsed?: string
}

export interface ISensemakerReview {
  status: 'pending' | 'accepted' | 'challenged'
  reviewerId?: string
  notes?: string
}

export type InsightType = 'question' | 'instrument' | 'question_crosstab'

export type InsightPriority = 'HIGH' | 'MEDIUM'

export type InsightReviewStatus = 'pending' | 'approved' | 'rejected'

export interface IInsightNote {
  _id: string
  authorId: string
  authorName: string
  authorRole: UserRole
  text: string
  createdAt: Date
  updatedAt: Date
}

export interface IQuantitativeBacking {
  p_value?: number | null
  ci?: string | null
  distributions?: Record<string, number> | null
}

/** Inline visual element on a cross-tab insight card (TC-QD-31). */
export interface IInsightVisualDataItem {
  label: string
  value: number
  maxValue?: number
}

/** SQS return envelope discriminators on `response_ms_return_queue` (co-owned with Rule 13). */
export type ResponseMsReturnMessageType =
  | 'pii_result'
  | 'question_insight'
  | 'instrument_insight'
  | 'question_text_analytics'
  | 'question_crosstab_insight'

export interface IInsight {
  _id: string
  campaignId: string
  loiId?: string
  instrumentId?: string
  questionId?: string | null
  type?: InsightType
  claim: string
  /** @deprecated Prefer evidenceLinks */
  evidence?: IEvidence[]
  evidenceLinks?: string[]
  /** @deprecated Prefer quantitativeBacking */
  quantitative_support?: IQuantitativeSupport
  quantitativeBacking?: IQuantitativeBacking
  /** @deprecated Prefer confidenceScore */
  confidence?: number
  confidenceScore?: number
  competing_interpretations: string[]
  /** @deprecated Prefer reviewStatus */
  sensemaker_review?: ISensemakerReview
  reviewStatus?: InsightReviewStatus
  methodology: string | Record<string, unknown>
  previousInsightId?: string | null
  priority?: InsightPriority
  sourceTags?: string[]
  /** TC-QD-31 — inline progress bars / segment comparison mini-cards on cross-tab insights. */
  visualData?: IInsightVisualDataItem[]
  suppressedNote?: string | null
  isFinalAnalysis?: boolean
  notes?: IInsightNote[]
  lastEvaluatedAt?: Date
  createdAt: Date
  updatedAt?: Date
}

export enum LoisRecommendationStatus {
  Confirmed = 'CONFIRMED',
  Partial = 'PARTIAL',
}

export interface IInstrumentRecommendation {
  /** Brownfield campaign-design recommendation (claim/evidence). Dashboard UI maps to `IInstrumentRecommendationCard`. */
  _id: string
  campaignId: string
  instrumentId: string
  claim: string
  evidenceLinks: string[]
  confidenceScore: number
  methodology: string
  status: LoisRecommendationStatus
  notes: IInsightNote[]
  lastEvaluatedAt: Date
  createdAt: Date
  updatedAt: Date
}

/** LOI / instrument dashboard — per-question majority or leading answer summary (BFP-483). */
export type QuestionAnswerSummary =
  | { kind: 'majority'; answerLabel: string; percent: number }
  | { kind: 'leading'; answerLabel: string; percent: number }
  | { kind: 'na' }

export interface IInstrumentDashboardMetrics {
  submissionCount: number
  lastSubmittedAt: string | null
  avgCompletionSeconds: number | null
  questions: Record<
    string,
    {
      responseCount: number
      avgTimeSeconds: number | null
      optionCounts: Record<string, number>
    }
  >
  respondentGenderSplit?: Array<{
    key: string
    label: string
    count: number
    percent: number
  }>
}

export interface LoisRecommendationRequestPayload {
  campaign_id: string
  loi_id: string
  instrument_insight_summaries: Array<{
    instrument_id: string
    instrument_name: string
    claim: string
    confidence_score: number
  }>
  campaign_info: Record<string, unknown>
}

/**
 * @deprecated Legacy reward document shape — not persisted. Use `sp_reward_accounts`,
 * `sp_balance_credit_events`, and `sp_redemptions` in Platform MS (BFP-300).
 */
export interface IReward {
  _id: string
  profileId: string
  responseId: string
  amount: number
  status: 'pending' | 'fulfilled' | 'failed' | 'deferred'
  provider: 'tremendous'
  externalId?: string
  anonymousToken?: string
  createdAt: Date
  updatedAt: Date
}

// ─── Team Management ────────────────────────────────────────────────────────

export enum TeamMemberStatus {
  Pending = 'pending',
  Active = 'active',
  Deactivated = 'deactivated',
  Expired = 'expired',
}

export type TeamMemberRole =
  | UserRole.SensemakerEditor
  | UserRole.SensemakerViewer

export interface ITeamMember {
  _id: string
  sensemakerId?: string
  firstName: string
  lastName: string
  email: string
  role: TeamMemberRole
  status: TeamMemberStatus
  platformUserId?: string
  joinDate?: Date
  invitedAt: Date
  invitedBy: string
  deactivatedAt?: Date
  deactivatedBy?: string
  reactivatedAt?: Date
  createdAt: Date
  updatedAt: Date
}

// ─── Domain Event Payloads ──────────────────────────────────────────────────

export interface TeamMemberDeactivatedPayload {
  teamMemberId: string
  platformUserId: string
  tenantId: string
  email: string
  role: TeamMemberRole
}

export interface TeamMemberReactivatedPayload {
  teamMemberId: string
  platformUserId: string
  tenantId: string
  email: string
  role: TeamMemberRole
}

// SQS Event Payloads
export interface CampaignCreatedPayload {
  campaignId: string
  goal: string
  loiIds: string[]
}

export interface InstrumentPublishedPayload {
  instrumentId: string
  campaignId: string
  loiId: string
  surveyToken: string
  rewardAmount: number
  sampleSize: number
}

export interface ResponseSubmittedPayload {
  responseId: string
  instrumentId: string
  /** Null for BFP-999 quick instruments (no parent campaign). */
  campaignId: string | null
  /** Null for BFP-999 quick instruments. */
  loiId: string | null
  profileId: string | null
  anonymousToken: string | null
  rewardAmount: number
  /**
   * Tenant context (BFP-257). Optional for backward compatibility, but required by the
   * platform reward-payout consumer to resolve the tenant-scoped wallet DB. Response MS
   * populates both from the response document / SP session.
   */
  tenantId?: string
  tenantSubdomain?: string
  /** BFP-294 — survey history display fields (response-service populates on submit). */
  submittedAt?: string
  instrumentName?: string
  rewardEnabled?: boolean
  rewardDisplay?: string | null
  /** BFP-1025-BE — snapshot when instrument `isSignupSurvey=true`. */
  isSignupSurvey?: boolean
  /**
   * BFP-1025-BE — when `true`, platform defers `creditSurveyReward` until eKYC Pass
   * (TC-BF-XX-12..15).
   */
  rewardCreditDeferred?: boolean
  /** BFP-1025-BE — defer branch key; `ekyc_required` when eKYC gate applies. */
  rewardDeferReason?: import('./signup-survey-pending-reward').RewardDeferReason
}

export interface RewardFulfilledPayload {
  rewardId: string
  profileId: string
  amount: number
  method: string
}

export interface RewardFailedPayload {
  rewardId: string
  profileId: string
  amount: number
  reason: string
}

export interface InsightGeneratedPayload {
  insightId: string
  campaignId: string
  loiId?: string
  confidence: InsightConfidence
  claim: string
}

// ─── Notification Queue Payloads ────────────────────────────────────────────

export type EmailNotificationType = 'simple_email' | 'html_email'

/**
 * Canonical SQS message for the `email-notification` queue.
 * Any service that wants to deliver an email publishes this payload —
 * the notification service is the sole consumer.
 *
 * Type mapping:
 *  - simple_email → subject + plain text body (optional HTML alternative)
 *  - html_email   → subject + full HTML body (optional plain-text fallback)
 */
export interface EmailNotificationQueuePayload {
  type: EmailNotificationType
  /** Recipient email address (must be verified in SES sandbox) */
  to: string
  subject: string
  /** Plain text body — required for simple_email; optional fallback for html_email */
  text?: string
  /** HTML body — required for html_email; optional alternative for simple_email */
  html?: string
  /** Reply-to address; defaults to the SES from-address when omitted */
  replyTo?: string
  /** Idempotency key — use to deduplicate on the consumer side */
  notificationId?: string
  tenantId?: string
  profileId?: string
}

// SMS Notification Types

export type SmsNotificationType = 'transactional_sms' | 'promotional_sms'

/**
 * Canonical SQS message for the `sms-notification` queue.
 * Any service that wants to deliver an SMS publishes this payload —
 * the notification service is the sole consumer.
 *
 * Type mapping:
 *  - transactional_sms → TRANSACTIONAL priority (reward confirmations, system alerts)
 *  - promotional_sms   → PROMOTIONAL priority (survey invitations, campaign alerts)
 *
 * AWS End User Messaging SMS classifies messages as TRANSACTIONAL or PROMOTIONAL.
 * TRANSACTIONAL bypasses quiet-hours and some opt-out rules in supported regions.
 */
export interface SmsNotificationQueuePayload {
  type: SmsNotificationType
  /** Recipient phone number in E.164 format (e.g. +12125551234) */
  phoneNumber: string
  /** Message text — up to 1,600 characters; long messages are split into segments by AWS */
  body: string
  /** Idempotency key — use to deduplicate on the consumer side */
  notificationId?: string
  tenantId?: string
  profileId?: string
}

// Push Notification Types

export type PushNotificationType =
  | 'normal_push'
  | 'rich_push'
  | 'normal_inapp'
  | 'rich_inapp'

export interface PushNotificationAction {
  /** Unique identifier the app uses to handle the action tap */
  id: string
  title: string
  /** Optional deep-link / universal URL opened when action is tapped */
  url?: string
}

/**
 * Canonical SQS message for the `push-notification` queue.
 * Any service that wants to deliver a push or in-app notification
 * publishes this payload — the notification service is the sole consumer.
 *
 * FCM type mapping:
 *  - normal_push  → `notification` field only (OS renders)
 *  - rich_push    → `notification` + image + platform-specific config
 *  - normal_inapp → data-only silent message (app renders in its inbox)
 *  - rich_inapp   → data-only silent message with media payload
 */
export interface PushNotificationQueuePayload {
  type: PushNotificationType
  /** FCM registration token of the target device */
  fcmToken: string
  title: string
  body: string
  /** Image URL — required for rich_push / rich_inapp */
  imageUrl?: string
  /** Universal link or in-app route opened on notification tap */
  deepLink?: string
  /** Action buttons (max 2 iOS, 3 Android) — for rich types */
  actions?: PushNotificationAction[]
  /**
   * Arbitrary string key-value pairs forwarded verbatim to the app.
   * All values MUST be strings (FCM data payload requirement).
   */
  data?: Record<string, string>
  /** Idempotency key — use to deduplicate on the consumer side */
  notificationId?: string
  tenantId?: string
  profileId?: string
}

// Bell (in-app) Notification Types — see `bell.ts` (BFP-382-BE extended union)
export type {
  AccountSetupRequiredBellQueuePayload,
  BellNotificationQueuePayload,
  BellNotificationQueuePayloadUnion,
  BellNotificationType,
  LegacyBellNotificationQueuePayload,
  ProfileUpdateRequiredBellQueuePayload,
  RewardCreditedBellQueuePayload,
  RichBellFields,
  SurveyCompletedBellQueuePayload,
} from './bell'
export type {
  AudienceAvailability,
  AudienceFilters,
  AiAudienceSuggestResponse,
  AudienceMatchPoolStatus,
  AudienceMatchResponse,
  DemographicPanelId,
  DeviceChannel,
  DistributionChannelId,
  PublishChannelContent,
  PublishConfigDraft,
  PublishConfigTargetAudience,
  PublishEmailContent,
  PublishPushContent,
  PublishSmsContent,
  SenateDistrictOption,
} from './audience-publish'
export {
  CHANNEL_CONTENT_LIMITS,
  AUDIENCE_AVAILABILITY_ALL_REGISTERED,
  AUDIENCE_AVAILABILITY_EXISTING_AND_FUTURE,
  AUDIENCE_AVAILABILITY_EXISTING_ONLY,
  AUDIENCE_AVAILABILITY_VALUES,
  FUTURE_MATCHING_ASSIGNMENT_SOURCES,
  INDEPENDENT_POLITICAL_PARTY_VALUES,
  SENATE_DISTRICT_OPTIONS,
} from './audience-publish'
export {
  CURRENT_VOTER_REGISTRATION_VALUES,
  REGISTERED_TO_VOTE_VALUES,
  extractVoterProfileAttributes,
  normalizeCurrentVoterRegistration,
  normalizeRegisteredToVote,
  normalizeVoterPrompt,
  pickLatestVoterProfileAttributes,
  voterAttributeForPrompt,
} from './voter-profile'
export type {
  CurrentVoterRegistrationValue,
  ExtractedVoterProfileAttributes,
  RegisteredToVoteValue,
  VoterAnswerSnapshot,
  VoterProfileAttributeKey,
  VoterQuestionSnapshot,
  VoterResponseForLatest,
} from './voter-profile'
export type { FutureMatchingAssignmentSource, IAssignFutureMatchingBody } from './audience-publish'
export type {
  FreshnessTag,
  IProfileAttribute,
  IProfileAttributeCurrentValue,
  IProfileAttributeListResponse,
  IProfileAttributeSimilarMatch,
  IProfileAttributeSimilarResponse,
  IRecordProfileAttributeAnswerBody,
  ProfileAttributeFilter,
  ProfileSystemKey,
  ProfileUpdateQuestionType,
  ResolvedProfileAttributeFilter,
  SystemProfileAttributeSeed,
  SystemProfileQuestionType,
} from './profile-attributes'
export {
  CURRENT_VOTER_REGISTRATION_OPTIONS,
  FRESHNESS_TAGS,
  PREFER_NOT_TO_ANSWER,
  PROFILE_CHILDREN_OPTIONS,
  PROFILE_EDUCATION_LEVEL_OPTIONS,
  PROFILE_EMPLOYMENT_STATUS_OPTIONS,
  PROFILE_GENDER_OPTIONS,
  PROFILE_GENDER_TO_STORED,
  PROFILE_HOUSEHOLD_SIZE_OPTIONS,
  PROFILE_HOUSING_STATUS_OPTIONS,
  PROFILE_MARRIAGE_HISTORY_OPTIONS,
  PROFILE_RACE_ETHNICITY_OPTIONS,
  PROFILE_RELATIONSHIP_STATUS_OPTIONS,
  PROFILE_SYSTEM_KEYS,
  PROFILE_UPDATE_QUESTION_TYPES,
  PROFILE_ZIP_CODE_OPTIONS,
  REGISTERED_TO_VOTE_OPTIONS,
  SYSTEM_PROFILE_ATTRIBUTE_SEEDS,
  SYSTEM_PROFILE_QUESTION_TYPES,
  RESERVED_PROFILE_STORAGE_KEYS,
  camelCaseProfileAttributeKey,
  isFreeTextProfileAttribute,
  isFreshnessTag,
  isProfileSystemKey,
  isProfileUpdateQuestionType,
  isSafeProfileAttributeStorageKey,
  profileAttributeStorageKey,
  systemProfileAttributesAsPublic,
} from './profile-attributes'
export type {
  ResponseTargetingMode,
  ScientificConfidenceLevel,
  SampleSizeCalculationRequest,
  SampleSizeCalculationResponse,
  SampleSizeConfidenceTip,
} from './scientific-sample-size'
export { SCIENTIFIC_CONFIDENCE_LEVELS } from './scientific-sample-size'

/** @deprecated Use `DeviceChannel`. */
export type DeviceTypeValue = import('./audience-publish').DeviceChannel
export {
  activeAudienceFiltersFromTargetAudience,
  canonicalizeTargetAudienceForStorage,
  isSenateDistrictOption,
  normalizeDeviceTypeFilter,
  readAiSuggestedFilters,
  readAudienceAvailability,
} from './audience-publish'
export {
  AUDIENCE_FILTER_CATALOG,
  AUDIENCE_SUGGEST_BROAD_RATIO,
  AUDIENCE_SUGGEST_MAX_PANELS,
  AUDIENCE_SUGGEST_MAX_VALUES_PER_PANEL,
  isBroadAudienceSelection,
  pruneBroadAudienceFilters,
  pruneSuggestedPanelIds,
} from './audience-filter-catalog'
export type { AudienceCheckboxPanelId } from './audience-filter-catalog'
export {
  resolveDemographicSegmentDisplayLabel,
  HOUSEHOLD_SIZE_DISPLAY_LABELS,
  NUMBER_OF_CHILDREN_DISPLAY_LABELS,
} from './audience-filter-display-labels'
export {
  CITIES_BY_STATE,
  US_STATE_OPTIONS,
  getCitiesForState,
  resolveStateCode,
} from './us-location-catalog'
export type { UsLocationOption } from './us-location-catalog'

export type {
  ICampaignBudgetSummary,
  IOtherInstrumentsInLoi,
  IWalletBalance,
  IWalletSufficiency,
  IWalletTransaction,
  IWalletTransactionsPage,
  WalletReservationStatus,
  WalletTransactionStatus,
  WalletTransactionType,
} from './wallet'

export type {
  IInstrumentLiveEditState,
  IInstrumentRespondentIdsResponse,
  IRepublishInstrumentResult,
  IResizeInstrumentSlotsBody,
  IResizeInstrumentSlotsResult,
  InstrumentLiveEditSurveyType,
  InstrumentPublishDistributionMode,
  InstrumentRespondentStatus,
  IInstrumentPublishDistributionJob,
  IInstrumentReminderJob,
  IInstrumentPublishDistributionProgress,
  IInstrumentPublishNotificationsEnqueued,
  IInstrumentPublishResult,
  InstrumentPublishDistributionStatus,
  InstrumentPublishNotificationsDispatch,
  ISignalProviderMatchRecipientsResponse,
  ISignalProviderRecipient,
  IWalletReleaseRequest,
  IWalletReleaseResult,
  IWalletReserveRequest,
  IWalletReserveResult,
} from './instrument-publish'

export type {
  ICreateQuickInstrumentBody,
  InstrumentPauseReason,
  IPatchSignupSurveyBody,
  IPublicSignupSurveyResolve,
  IQuickInstrumentListItem,
  IQuickInstrumentListQuery,
  IQuickInstrumentListResult,
  IReactivateInstrumentBody,
  SignupSurveyBlockReason,
} from './instrument-origin'
export { InstrumentOrigin, SIGNUP_SURVEY_MIN_REWARD_USD } from './instrument-origin'

// BFP-257 / BFP-310 — Signal Provider onboarding + slot-based survey execution
export type {
  InstrumentCloseReason,
  InstrumentLinkTokenClaims,
  InstrumentLinkTokenRedisPayload,
  IInstrumentDefinition,
  IInstrumentSlotsClosePayload,
  IInstrumentSlotsCloseResult,
  IInstrumentSlotsInitializePayload,
  IInstrumentTenantContext,
  IProviderAnswerInput,
  IProviderAvailableSurvey,
  IProviderInstrumentEngagement,
  IProviderSurveyHistoryItem,
  IProviderSurveyHistoryPage,
  IWebSurveyAnonymousSessionData,
  IWebSurveyAnonymousSessionRequest,
  IWebSurveyAvailabilityData,
  IWebSurveyClaimPendingData,
  IWebSurveyClaimPendingRequest,
  IWebSurveyEmailCheckRequest,
  IWebSurveyPhoneCheckRequest,
  IWebSurveyPromotePayload,
  IWebSurveyPromoteResult,
  PendingSignupSurveyStatus,
  ProviderSurveyCompletionStatus,
  IProviderInstrumentInfo,
  IProviderInstrumentStatus,
  IProviderGetResponseData,
  IProviderStartResponseData,
  IProviderNextResult,
  IProviderResponseState,
  IProviderReward,
  IProviderSubmitConfirmation,
  ISpResponseTenantContext,
  ProviderAbandonReason,
  ProviderResponseStatus,
  SpOnboardingChannel,
} from './sp-onboarding'
export type {
  ISpDemographicStep,
  ISpLocationData,
  ISpProfileCompleteness,
  ISpProfilePublic,
  ISpRewardsSummary,
  PatchSpDemographicsInput,
  SpDemographicFieldId,
  SpDemographicStepKind,
} from './sp-profile'
export { SP_MIN_REDEEM_USD } from './sp-profile'
export type {
  AccountDeletionScheduledPayload,
  SpAccountDeletionCancelResult,
  SpAccountDeletionRequestInput,
  SpAccountDeletionRequestResult,
  SpAccountAnonymizeDeletionDataInput,
  SpAccountAnonymizeDeletionDataResult,
  SpAccountForfeitBalanceInput,
  SpAccountForfeitBalanceResult,
  SpLogoutAllDevicesResult,
  SpMobileOtpResendInput,
  SpMobileOtpSendInput,
  SpMobileOtpSendResult,
  SpMobileOtpVerifyInput,
  SpMobileOtpVerifyResult,
  SpSupportAttachmentUploadResult,
  SpSupportInquiryJsonInput,
  SpSupportInquiryResult,
  SpSupportInquirySubject,
} from './sp-account-settings'
export type {
  ISpClaimedReward,
  ISpClaimedRewardsPage,
  ISpRewardBrand,
  RedeemSpRewardInput,
  RewardRedeemedAppFlavor,
  RewardRedeemedBellQueuePayload,
  RewardRedeemedPhase,
  RewardRedeemedPushAction,
  RewardRedeemedPushData,
  RewardRedeemedPushQueuePayload,
  SpRedemptionStatus,
} from './sp-rewards'
export type {
  ISpBalanceCreditEvent,
  ISpBalanceDebitEvent,
  ISpLedgerEntry,
  ISpLedgerPage,
  ISpManualBalanceAdjustInput,
  ISpManualBalanceAdjustResult,
  SpBalanceCreditType,
  SpBalanceDebitType,
  SpLedgerEntryKind,
  SpRedemptionAttemptStatus,
} from './sp-ledger'

// BFP-485 — Individual Question Drill-Down Dashboard
export type {
  EngagementIndex,
  IDemographicFilterTab,
  IDistributionBucket,
  IProviderDemographicSnapshot,
  CrossTabInsightPriority,
  ICrossTabQualificationSummary,
  IQualifiedCrossTabComparison,
  IQuestionCrossTabStats,
  IQuestionCrosstabQualificationResult,
  IQuestionDashboard,
  IQuestionRecommendation,
  IQuestionDistribution,
  IQuestionDrilldownMetrics,
  IQuestionMetricTiles,
  IQuestionNote,
  IQuestionSentiment,
  IQuestionTextAnalytics,
  IQuestionTheme,
  IWordCloudTerm,
  QuestionChartType,
  QuestionDataNature,
  QuestionDemographicDimension,
  QuestionSentimentLabel,
  TextAnalyticsAiSource,
} from './question-dashboard'

// BFP-653 — LOI Level Response Dashboard
export type {
  BurndownGranularity,
  IBurndownInstrumentSeries,
  IBurndownSeriesPoint,
  ILoiBurndownSeries,
  ILoiDashboardMetrics,
  ILoiDashboardSummary,
  ILoiInsightAiCard,
  ILoiInsightCard,
  ILoiInsightNote,
  ILoiInsightsGenerationRequest,
  ILoiInsightsGenerationResponse,
  ILoiInsightsGenerationResult,
  ILoiInsightsPanel,
  ILoiInstrumentCard,
  ILoiInstrumentQuestionRow,
  ILoiRecommendation,
  ILoiRecommendationAiCard,
  ILoiRecommendationGenerationRequest,
  ILoiRecommendationGenerationResponse,
  ILoiRecommendationGenerationResult,
  ILoiRecommendationsPanel,
  ILoiTeamNote,
  ILoiTopAnswerMatrix,
  ITopAnswerMatrixRow,
  LoiDashboardInstrumentStatus,
  LoiInsightFindingType,
  LoiInstrumentStatusFilter,
  LoiRecommendationBadge,
  LoiRecommendationCategory,
  TopAnswerMatrixCell,
} from './loi-dashboard'

// BFP-654 — Instrument Level Response Dashboard
export type {
  DemographicFilterQuery,
  IInstrumentDashboardQuestionRow,
  QuestionOptionPreviewBar,
  IInstrumentDashboardSummary,
  IInstrumentInsightAiCard,
  IInstrumentInsightCard,
  IInstrumentInsightGenerationResult,
  IInstrumentInsightNote,
  IInstrumentInsightReEvaluateResponse,
  IInstrumentInsightsPanel,
  IInstrumentRecommendationAiCard,
  IInstrumentRecommendationCard,
  IInstrumentRecommendationGenerationRequest,
  IInstrumentRecommendationGenerationResult,
  IInstrumentRecommendationsPanel,
  IRespondentGenderSegment,
  InstrumentDashboardStatus,
  InstrumentRecommendationBadge,
  InstrumentRecommendationCategory,
} from './instrument-dashboard'

export type { IAdminDashboardMetrics } from './admin-dashboard'

export type {
  ISensemakerListCard,
  ISensemakerListResponse,
  ISensemakerListSummary,
  SensemakerCardStatus,
  SensemakerPackageTier,
} from './sensemaker-list'

export type {
  ISensemakerCampaignOverviewPayload,
  ISensemakerOverviewCampaignCard,
  ISensemakerOverviewInstrumentRow,
  ISensemakerOverviewInstrumentSplit,
  ISensemakerOverviewLoiCard,
  ISensemakerOverviewMetrics,
  ISensemakerOverviewProfile,
  ISensemakerOverviewResponse,
  ISensemakerOverviewTeamMember,
  SensemakerInstrumentBuilderType,
  SensemakerOverviewCampaignStatus,
  SensemakerOverviewInstrumentStatus,
  SensemakerOverviewLoiInstrumentTypePill,
  SensemakerTypeTag,
} from './sensemaker-overview'

export type {
  ISignalProviderDetailResponse,
  ISignalProviderDetailStats,
  ISignalProviderListCard,
  ISignalProviderListDemographics,
  ISignalProviderListResponse,
  ISignalProviderListSummary,
  SignalProviderAccountStatus,
  SignalProviderVerifiedStatus,
} from './signal-provider-list'

export type {
  AudienceEkycStatusFilter,
  EkycFailureReason,
  EkycStatus,
} from './ekyc'

export {
  SensemakerSignupSource,
  SENSEMAKER_SIGNUP_SOURCE_LABELS,
} from './platform-people-accounts-dashboard'
export type {
  IPlatformDashboardDailySignupBucket,
  IPlatformDashboardDemographicCompletenessRow,
  IPlatformDashboardGrowthTrend,
  IPlatformDashboardSignupSourceSegment,
  IPlatformDashboardTimeBucket,
  IPlatformPeopleAccountsDashboard,
  PlatformDashboardGrowthPeriod,
  SignalProviderSignupChannel,
} from './platform-people-accounts-dashboard'

export {
  INSTRUMENT_LINK_TOKEN_PURPOSE,
  OPAQUE_INSTRUMENT_LINK_TOKEN_BYTE_LENGTH,
  SP_INSTRUMENT_LINK_TOKENS_PREFIX,
  instrumentAvailableSlotsRedisKey,
  instrumentEligibleProvidersRedisKey,
  instrumentEligibleStagingRedisKey,
  instrumentTenantContextRedisKey,
  instrumentSubmissionCountRedisKey,
  isAbandonedProviderResponseStatus,
  isActiveProviderResponseStatus,
  isJwtInstrumentLinkToken,
  isOpaqueInstrumentLinkToken,
  isPendingProviderId,
  pendingProviderId,
  PENDING_PROVIDER_PREFIX,
  spActiveSessionRedisKey,
  spInstrumentLinkTokenRedisKey,
  spProviderEligibleInstrumentsRedisKey,
  spResponseTenantContextRedisKey,
} from './sp-onboarding'

// ─── Instrument templates (BFP-273) ─────────────────────────────────────────

export enum InstrumentTemplateCategory {
  BrandCreative = 'brand_creative',
  PricingValue = 'pricing_value',
  CustomerExperience = 'customer_experience',
  ProductFeature = 'product_feature',
}

export const INSTRUMENT_TEMPLATE_CATEGORY_LABELS: Record<
  InstrumentTemplateCategory,
  string
> = {
  [InstrumentTemplateCategory.BrandCreative]: 'Brand & Creative',
  [InstrumentTemplateCategory.PricingValue]: 'Pricing & Value',
  [InstrumentTemplateCategory.CustomerExperience]: 'Customer Experience',
  [InstrumentTemplateCategory.ProductFeature]: 'Product & Feature',
}

export interface IInstrumentTemplateSummary {
  id: string
  slug: string
  category: InstrumentTemplateCategory
  name: string
  shortDescription: string
  /** Estimated completion time in seconds (sum of per-question ECT). */
  estCompletionSecs: number
  questionCount: number
  iconKey: string
  sortOrder: number
  /** Platform Admin only — hidden from Sensemaker template library (BFP-579). */
  hidden?: boolean
  /** Platform Admin only — count of published instruments created from this template (BFP-579). */
  publishedUsageCount?: number
}

/** PATCH body for Platform Admin hide/unhide (BFP-579). */
export interface IInstrumentTemplateVisibilityPatch {
  hidden: boolean
}

/** Preview / clone — questions omit respondent ids until materialized on instrument create. */
export interface IInstrumentTemplateQuestionSeed {
  order: number
  type: string
  prompt: string
  description?: string | null
  mandatory?: boolean
  /** BFP-1070 — exclude from dashboards/reports; still shown to responders. Missing = false. */
  hidden?: boolean
  options?: Array<{ label: string; imageUrl?: string }>
  randomizeOrder?: boolean
  layoutHorizontal?: boolean
  imageSize?: 'small' | 'medium' | 'large'
  ratingScaleMax?: 5 | 10
  ratingDisplayStyle?: 'stars' | 'numbers'
  ratingLowLabel?: string
  ratingHighLabel?: string
  likertPointCount?: number
  likertLabels?: string[]
  likertLayoutHorizontal?: boolean
  sliderMinValue?: number
  sliderMaxValue?: number
  sliderStep?: number
  sliderLowLabel?: string
  sliderHighLabel?: string
  minLength?: number
  maxLength?: number
  placeholder?: string
  yesLabel?: string
  noLabel?: string
  /** When `type` is `video` — YouTube/Vimeo/MP4 URL or uploaded serve path. */
  videoUrl?: string
  /** When `type` is `video` — how the source was added. */
  videoSource?: 'link' | 'upload'
}

export interface IInstrumentTemplateDetail extends IInstrumentTemplateSummary {
  questions: IInstrumentTemplateQuestionSeed[]
}

export interface IInstrumentTemplateListResponse {
  categories: Array<{
    category: InstrumentTemplateCategory
    label: string
    templates: IInstrumentTemplateSummary[]
  }>
}

export interface CreateInstrumentFromTemplateInput {
  name: string
  loiId: string
  description?: string
  templateId?: string
  /** BFP-606 — create from a user-saved Built-in Instrument snapshot. */
  builtInInstrumentId?: string
}

/** BFP-601 — full question object stored in Question Bank (extends template seed with builder fields). */
export interface IQuestionBankQuestionPayload extends IInstrumentTemplateQuestionSeed {
  minSelections?: number
  maxSelections?: number
  showSelectAll?: boolean
  selectAllLabel?: string
  multiColumn?: boolean
  yesNoToggleStyle?: boolean
  hasOtherOption?: boolean
  hasNoneOfAboveOption?: boolean
  earliestSelectableDate?: string
  latestSelectableDate?: string
  disablePastDates?: boolean
  disableFutureDates?: boolean
  dateInputFormat?: 'dd/MM/yyyy' | 'MM/dd/yyyy'
}

/** Latest published survey that contained this Question Bank entry. */
export type QuestionBankLastUsedSurveyType = 'sign_up' | 'direct' | 'regular' | 'dynamic'

/** BFP-601 — list card / picker row. */
export interface IQuestionBankEntrySummary {
  id: string
  type: string
  prompt: string
  usageCount: number
  lastUsedAt: string | null
  /**
   * Survey type of the latest successful publish that contained this question.
   * Omitted or null when the question has never been published.
   */
  lastUsedSurveyType?: QuestionBankLastUsedSurveyType | null
  savedByName: string
  createdAt: string
  /** BFP-1070 — true when the saved question has Hide Question enabled. */
  hidden: boolean
  /** BFP-1143 — true when this bank entry is a Profile Update Question. */
  isProfileUpdate?: boolean
  /** BFP-1143 — linked profile attribute. */
  profileAttributeId?: string | null
}

/** BFP-601 — detail modal. */
export interface IQuestionBankEntryDetail extends IQuestionBankEntrySummary {
  question: IQuestionBankQuestionPayload
  savedByUserId: string
}

/** BFP-601 — POST body from Question Builder bookmark save. */
export interface CreateQuestionBankEntryInput {
  prompt: string
  type: string
  question: IQuestionBankQuestionPayload
  forceDuplicate?: boolean
}

/** BFP-601 — list response envelope. */
export interface IQuestionBankListResponse {
  entries: IQuestionBankEntrySummary[]
}

/** BFP-606 — list card for Built-in Instruments section. */
export interface IBuiltInInstrumentSummary {
  id: string
  name: string
  questionCount: number
  estCompletionSecs: number
  usageCount: number
  savedByName: string
  createdAt: string
}

/** BFP-606 — preview modal detail. */
export interface IBuiltInInstrumentDetail extends IBuiltInInstrumentSummary {
  questions: IQuestionBankQuestionPayload[]
  savedByUserId: string
}

/** BFP-606 — POST body from Question Builder Add to Library. */
export interface CreateBuiltInInstrumentInput {
  name: string
  questions: IQuestionBankQuestionPayload[]
  questionCount: number
  estCompletionSecs: number
}

/** BFP-606 — list response envelope. */
export interface IBuiltInInstrumentListResponse {
  entries: IBuiltInInstrumentSummary[]
}

export type {
  ReportMode,
  ReportSectionKey,
  ReportSections,
  InstrumentReportType,
  LoiReportType,
  IReportInstrumentOption,
  IReportLoiOption,
  GenerateInstrumentReportRequest,
  GenerateLoiReportRequest,
  IInstrumentReportQuestionMetrics,
  IInstrumentReportMetrics,
} from './reports'
export { REPORT_SECTION_KEYS } from './reports'

export {
  CompanyStatus,
  CompanyType,
  COMPANY_TYPES,
  COMPANY_INDUSTRIES,
  COMPANY_RESEARCH_DOMAINS,
  MAX_COMPANY_RESEARCH_DOMAINS,
} from './company'
export type { CompanyIndustry, CompanyResearchDomain } from './company'

// BFP-1025-BE — signup-survey eligibility + deferred reward flags
export type {
  SignupSurveyCardPayload,
  SignupSurveyCardReward,
  SignupSurveyEligibilityData,
  SignupSurveyEligibilityRequest,
  SignupSurveyEligibilitySuccessEnvelope,
} from './signup-survey'
export type {
  PendingSignupSurveyRewardRecord,
  PendingSignupSurveyRewardStatus,
  ResponseSubmittedSignupSurveyExtension,
  RewardDeferReason,
} from './signup-survey-pending-reward'
